import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMapEvents } from 'react-leaflet';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icon in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Component to handle map clicks
function LocationMarker({ position, onPositionChange }) {
  useMapEvents({
    click(e) {
      onPositionChange(e.latlng);
    },
  });

  return position === null ? null : <Marker position={position} />;
}

const LocationMapPicker = ({ 
  location, 
  onLocationChange, 
  radius = 30, 
  onRadiusChange,
  language = 'en' 
}) => {
  // Default to center of Switzerland (Bern)
  const [position, setPosition] = useState(
    location?.lat && location?.lng 
      ? { lat: location.lat, lng: location.lng }
      : { lat: 46.8182, lng: 8.2275 }
  );

  const [selectedRadius, setSelectedRadius] = useState(radius);

  // Update position when location prop changes (but only if different)
  useEffect(() => {
    if (location?.lat && location?.lng) {
      // Only update if coordinates are actually different
      if (position.lat !== location.lat || position.lng !== location.lng) {
        setPosition({ lat: location.lat, lng: location.lng });
      }
    }
  }, [location?.lat, location?.lng]);

  // Handle position changes from map clicks
  const handlePositionChange = (newPosition) => {
    setPosition(newPosition);
    onLocationChange({
      lat: newPosition.lat,
      lng: newPosition.lng
    });
  };

  const handleRadiusChange = (value) => {
    setSelectedRadius(parseInt(value));
    if (onRadiusChange) {
      onRadiusChange(parseInt(value));
    }
  };

  const translations = {
    en: {
      title: 'Select your service area on the map',
      instruction: 'Click on the map to select your location',
      radius: 'Service Radius',
      currentLocation: 'Selected Location'
    },
    de: {
      title: 'Wählen Sie Ihr Arbeitsbereich auf der Karte',
      instruction: 'Klicken Sie auf die Karte, um Ihren Standort auszuwählen',
      radius: 'Service-Radius',
      currentLocation: 'Ausgewählter Standort'
    }
  };

  const t = translations[language] || translations.en;

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm font-medium">{t.title}</Label>
        <p className="text-xs text-gray-500 mb-2">{t.instruction}</p>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-3">
        <div>
          <Label className="text-xs">{t.radius}</Label>
          <Select value={selectedRadius.toString()} onValueChange={handleRadiusChange}>
            <SelectTrigger>
              <SelectValue placeholder="Select radius" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10 Km</SelectItem>
              <SelectItem value="20">20 Km</SelectItem>
              <SelectItem value="30">30 Km</SelectItem>
              <SelectItem value="50">50 Km</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label className="text-xs">{t.currentLocation}</Label>
          <div className="text-xs text-gray-600 p-2 bg-gray-50 rounded border">
            {position.lat.toFixed(4)}, {position.lng.toFixed(4)}
          </div>
        </div>
      </div>

      <div className="border-2 border-gray-200 rounded-lg overflow-hidden" style={{ height: '350px' }}>
        <MapContainer
          center={[position.lat, position.lng]}
          zoom={9}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <LocationMarker position={position} onPositionChange={handlePositionChange} />
          {position && (
            <Circle
              center={position}
              radius={selectedRadius * 1000}
              pathOptions={{ 
                fillColor: '#2563eb', 
                fillOpacity: 0.1,
                color: '#2563eb',
                weight: 2
              }}
            />
          )}
        </MapContainer>
      </div>

      <p className="text-xs text-gray-500 italic">
        {language === 'en' 
          ? `Your service area covers approximately ${selectedRadius} km radius from the selected location.`
          : language === 'it'
          ? `La tua area di servizio copre circa ${selectedRadius} km di raggio dalla posizione selezionata.`
          : language === 'fr'
          ? `Votre zone de service couvre environ ${selectedRadius} km de rayon depuis l'emplacement sélectionné.`
          : `Ihr Servicebereich umfasst ca. ${selectedRadius} km Radius vom ausgewählten Standort.`}
      </p>
    </div>
  );
};

export default LocationMapPicker;
